import { Component, OnInit } from "@angular/core";
import { ReleaseNote } from "#models/release-note.model";
import { ReleaseNoteService } from "#services/api/release-note.service";
import { AuthService } from "#services/shared";
import { MessageService } from "primeng/api";

interface ParsedReleaseSection {
  subtitle: string;
  items: string[];
}

interface ParsedReleaseSummary {
  title: string;
  sections: ParsedReleaseSection[];
}

@Component({
  selector: "app-release-notes",
  templateUrl: "./release-notes.component.html",
  styleUrls: ["./release-notes.component.scss"],
  standalone: false,
})
export class ReleaseNotesComponent implements OnInit {
  releaseNotes: ReleaseNote[] = [];
  parsedSummaries: Record<string, ParsedReleaseSummary> = {};
  selectedReleaseId: string | null = null;
  isLoading = false;
  isUploading = false;
  hasError = false;
  fileNotFound = false;
  private currentUserId: number | null = null;

  constructor(
    private readonly _releaseNoteService: ReleaseNoteService,
    private readonly _authService: AuthService,
    private readonly _messageService: MessageService
  ) {}

  ngOnInit(): void {
    const currentUser = this._authService.getCurrentUser();
    this.currentUserId = currentUser?.ID || null;
    this.loadAll();
  }

  trackById(_index: number, item: ReleaseNote): string {
    return item.id;
  }

  toggleRelease(releaseId: string): void {
    this.selectedReleaseId = releaseId;
  }

  isReleaseExpanded(releaseId: string): boolean {
    return this.selectedReleaseId === releaseId;
  }

  getDisplayTitle(title: string): string {
    return String(title || "").replace(/\.pdf$/i, "");
  }

  getReleaseHeaderTitle(note: ReleaseNote): string {
    const parsedTitle = this.getParsedSummary(note).title;
    return parsedTitle || this.getDisplayTitle(note.title);
  }

  triggerFileSelection(input: HTMLInputElement): void {
    if (this.isUploading) {
      return;
    }

    input.click();
  }

  onReleaseNoteFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    if (!this.isValidUploadFile(file.name)) {
      this._messageService.add({
        severity: "error",
        summary: "error!",
        detail: "unsupported file format, allowed: .pdf, .doc, .docx, .txt",
        life: 5000,
      });
      input.value = "";
      return;
    }

    this.isUploading = true;

    this._releaseNoteService.uploadAttachment(file).subscribe({
      next: (createdNote) => {
        const updatedReleaseNotes = [
          createdNote,
          ...this.releaseNotes.filter((note) => note.id !== createdNote.id),
        ];

        this.releaseNotes = updatedReleaseNotes;
        this.parsedSummaries = updatedReleaseNotes.reduce<Record<string, ParsedReleaseSummary>>(
          (accumulator, note) => {
            accumulator[note.id] = this.parseSummary(note.summary);
            return accumulator;
          },
          {}
        );
        this.selectedReleaseId = createdNote.id;
        this.isUploading = false;
        input.value = "";
        this._messageService.add({
          severity: "success",
          summary: "success!",
          detail: "release note uploaded successfully",
          life: 5000,
        });
      },
      error: () => {
        this.isUploading = false;
        input.value = "";
        this._messageService.add({
          severity: "error",
          summary: "error!",
          detail: "there was an error uploading release note",
          life: 5000,
        });
      },
    });
  }

  getParsedSummary(note: ReleaseNote): ParsedReleaseSummary {
    return (
      this.parsedSummaries[note.id] || {
        title: "",
        sections: [],
      }
    );
  }

  private parseSummary(rawSummary: string): ParsedReleaseSummary {
    const normalizedHtml = this.normalizeEscapedSummary(rawSummary);
    const paragraphRegex = /<p\b[^>]*>([\s\S]*?)<\/p>/gi;
    const paragraphs: Array<{ html: string; text: string }> = [];

    let match = paragraphRegex.exec(normalizedHtml);
    while (match) {
      const html = String(match[1] || "").trim();
      const text = this.htmlToText(html);
      if (text) {
        paragraphs.push({ html, text });
      }
      match = paragraphRegex.exec(normalizedHtml);
    }

    if (!paragraphs.length) {
      return { title: "", sections: [] };
    }

    const title = paragraphs[0].text;
    const sections: ParsedReleaseSection[] = [];
    let currentSubtitle = "";

    paragraphs.slice(1).forEach((paragraph) => {
      if (this.isSubtitleParagraph(paragraph.html, paragraph.text)) {
        currentSubtitle = paragraph.text.replace(/:\s*$/, "");
        sections.push({ subtitle: currentSubtitle, items: [] });
        return;
      }

      const text = paragraph.text;
      if (!text) {
        return;
      }

      if (!sections.length) {
        currentSubtitle = "Updates";
        sections.push({ subtitle: currentSubtitle, items: [] });
      }

      const activeSection = sections[sections.length - 1];
      activeSection.items.push(text);
    });

    const filteredSections = sections.filter((section) => section.items.length > 0);
    return { title, sections: filteredSections };
  }

  private isSubtitleParagraph(paragraphHtml: string, paragraphText: string): boolean {
    const normalizedText = String(paragraphText || "").trim();
    if (!normalizedText) {
      return false;
    }

    const hasStrongTag = /<strong\b[^>]*>/i.test(paragraphHtml);
    if (hasStrongTag) {
      return true;
    }

    return /^[A-Z][A-Za-z\s&-]{2,40}:$/.test(normalizedText);
  }

  private normalizeEscapedSummary(value: string): string {
    return String(value || "")
      .replace(/\\r\\n|\\n\\r|\\n|\\r/g, "\n")
      .replace(/\\"/g, '"')
      .replace(/\r\n|\n\r|\r/g, "\n");
  }

  private htmlToText(value: string): string {
    const normalized = String(value || "").replace(/<br\s*\/?\s*>/gi, "\n");
    const parsed = new DOMParser().parseFromString(normalized, "text/html");
    return String(parsed.body?.textContent || "")
      .replace(/\u00a0/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  private isValidUploadFile(fileName: string): boolean {
    return /\.(pdf|doc|docx|txt)$/i.test(String(fileName || ""));
  }

  private loadAll(): void {
    this.isLoading = true;
    this.hasError = false;
    this.fileNotFound = false;

    this._releaseNoteService.getAll(this.currentUserId || undefined).subscribe({
      next: (items) => {
        this.releaseNotes = items;
        this.parsedSummaries = items.reduce<Record<string, ParsedReleaseSummary>>(
          (accumulator, note) => {
            accumulator[note.id] = this.parseSummary(note.summary);
            return accumulator;
          },
          {}
        );
        this.selectedReleaseId = items.length ? items[0].id : null;
        this.isLoading = false;
      },
      error: () => {
        this.hasError = true;
        this.isLoading = false;
      },
    });
  }
}
